import { markBoxesMatch, type MarkBox, type StampSlot, type TemplatePageKinds } from '@provenance/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { MarkLayout } from './coordinates';

export interface Placement extends MarkBox {
  slotId: string;
  page: number;
  baseline?: number;
}

export type SyncState =
  | { kind: 'pending' }
  | { kind: 'in-sync'; checked: number }
  | { kind: 'out-of-sync'; label: string; editor: MarkBox; proof: MarkBox };

/**
 * How long the layout must sit still before the proof re-renders.
 *
 * Each render is a full server-side PDF stamp. Rendering on every drag frame would turn
 * moving a seal into a flood against the chamber's own API; waiting for a pause means one
 * render per adjustment, which is what an administrator wants to look at anyway.
 */
const SETTLE_MS = 700;

/**
 * The proof of the layout as it is on screen, saved or not (T-126).
 *
 * The proof pane used to render only the SAVED layout, and only when asked — so while an
 * administrator worked it showed the past, and said "stale". Now it follows the editor,
 * and checks it: the engine reports where it drew every mark, and each must match the box
 * the editor drew to within half a point.
 */
export function useDraftProof({
  documentTypeId,
  slots,
  layouts,
  enabled,
  pages,
}: {
  documentTypeId: string;
  slots: StampSlot[];
  layouts: Record<string, MarkLayout>;
  enabled: boolean;
  /** The template's page tags and the overleaf option, unsaved (T-137). */
  pages: { templatePageKinds: TemplatePageKinds | null; stampOverleaf: boolean };
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [placements, setPlacements] = useState<Placement[] | null>(null);
  const [renderedFor, setRenderedFor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const objectUrl = useRef<string | null>(null);
  const sequence = useRef(0);

  const signature = JSON.stringify({ slots, pages });

  const render = useCallback(async () => {
    const mine = ++sequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.post<{ pdfBase64: string; placements: Placement[] }>(
        `/api/console/configuration/stamping/${documentTypeId}/preview`,
        (() => {
          const parsed = JSON.parse(signature) as { slots: StampSlot[]; pages: Record<string, unknown> };
          return { slots: parsed.slots, ...parsed.pages };
        })(),
      );
      // A slower, older render must not replace a newer one.
      if (mine !== sequence.current) return;
      const bytes = Uint8Array.from(atob(result.pdfBase64), (c) => c.charCodeAt(0));
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      setUrl(objectUrl.current);
      setPlacements(result.placements);
      setRenderedFor(signature);
    } catch (err) {
      if (mine !== sequence.current) return;
      setError(err instanceof ApiError ? err.message : 'The proof could not be rendered. Your layout is unchanged.');
    } finally {
      if (mine === sequence.current) setLoading(false);
    }
  }, [documentTypeId, signature]);

  useEffect(() => {
    if (!enabled) return;
    const timer = window.setTimeout(() => void render(), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, render]);

  // A blob URL pins the whole PDF in memory until it is revoked.
  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const current = renderedFor === signature;
  let sync: SyncState = { kind: 'pending' };
  if (current && placements) {
    sync = { kind: 'in-sync', checked: 0 };
    for (const placement of placements) {
      // A mark prints on every page of its kind (T-137); the editor's box describes the
      // page it is edited on, so that is the one placement it is compared with.
      const own = slots.find((s) => s.id === placement.slotId);
      if (own && own.page !== placement.page) continue;
      const layout = layouts[placement.slotId];
      // Provisional boxes (an image still loading) are not a claim, so not checked.
      if (!layout || layout.provisional) continue;
      if (!markBoxesMatch(layout.box, placement)) {
        const slot = slots.find((s) => s.id === placement.slotId);
        sync = { kind: 'out-of-sync', label: slot?.label ?? slot?.slotType ?? 'A mark', editor: layout.box, proof: placement };
        break;
      }
      sync.checked += 1;
    }
  }

  return { url, loading, error, current, sync, render };
}
